import type { IsoDate } from "../dates";
import { newId, type AccountId } from "../ids";
import type { Transaction } from "../schemas";
import type { DocWriter } from "../doc";
import { ensurePayee, reconciliationPayeeId } from "./payees";
import { saveTransaction } from "./transactions";

export interface AccountBalances {
  cleared: number;
  uncleared: number;
  working: number;
}

/** Cleared (cleared + reconciled), uncleared and working balances (SPEC §6). */
export function accountBalances(
  transactions: Iterable<Transaction>,
  accountId: AccountId,
): AccountBalances {
  let cleared = 0;
  let uncleared = 0;
  for (const t of transactions) {
    if (t.deleted || t.accountId !== accountId) {
      continue;
    }
    if (t.cleared === "uncleared") {
      uncleared += t.amountAndSplits.amount;
    } else {
      cleared += t.amountAndSplits.amount;
    }
  }
  return { cleared, uncleared, working: cleared + uncleared };
}

export function balancesByAccount(
  transactions: Iterable<Transaction>,
): Map<AccountId, AccountBalances> {
  const out = new Map<AccountId, AccountBalances>();
  for (const t of transactions) {
    if (t.deleted) {
      continue;
    }
    const b = out.get(t.accountId) ?? { cleared: 0, uncleared: 0, working: 0 };
    if (t.cleared === "uncleared") {
      b.uncleared += t.amountAndSplits.amount;
    } else {
      b.cleared += t.amountAndSplits.amount;
    }
    b.working = b.cleared + b.uncleared;
    out.set(t.accountId, b);
  }
  return out;
}

export interface ReconcileResult {
  reconciled: number;
  adjustment: number;
}

/**
 * Finishes a reconciliation (SPEC §6): every cleared transaction becomes reconciled and, if
 * the bank balance differs, a reconciled balance adjustment makes up the difference (income
 * for inflows, Uncategorised for outflows, so it stays visible).
 */
export function finishReconcile(
  w: DocWriter,
  accountId: AccountId,
  bankBalance: number,
  date: IsoDate,
): ReconcileResult {
  const acc = w.get("accounts", accountId);
  const txns = w.list("transactions").filter((t) => t.accountId === accountId);
  const { cleared } = accountBalances(txns, accountId);
  let reconciled = 0;
  for (const t of txns) {
    if (t.cleared === "cleared") {
      w.put("transactions", t.id, { cleared: "reconciled" });
      reconciled++;
    }
  }
  const adjustment = bankBalance - cleared;
  if (adjustment !== 0) {
    ensurePayee(w, reconciliationPayeeId, "Reconciliation Balance Adjustment");
    saveTransaction(w, {
      id: newId("transaction"),
      accountId,
      date,
      amount: adjustment,
      payeeId: reconciliationPayeeId,
      ...(adjustment > 0 && acc?.onBudget ? { income: "thisMonth" as const } : {}),
      cleared: "reconciled",
      memo: "Balance adjustment",
      createdBy: w.origin.actorId,
    });
  }
  return { reconciled, adjustment };
}
